"use client";

import Link from "next/link";
import type { MarketingLocale } from "@/lib/seo/site";
import { BrandSocialIcons } from "@/components/marketing/BrandSocialIcons";
import { PressLanguageToggle } from "@/components/marketing/PressLanguageToggle";
import { PublicFooterLanguage } from "@/components/PublicFooterLanguage";
import { PRESS_COPY, type PressLang } from "@/lib/press/kit";
import { useLanguage } from "@/lib/useLanguage";

const FOOTER_BY_LOCALE: Record<
  MarketingLocale,
  readonly { href: string; label: string }[]
> = {
  es: [
    { href: "/download", label: "Descargar" },
    { href: "/community", label: "Comunidad" },
    { href: "/restaurants", label: "Restaurantes" },
    { href: "/press?lang=es", label: "Prensa" },
    { href: "/support", label: "Soporte" },
    { href: "/privacy", label: "Privacidad" },
    { href: "/terms", label: "Términos" },
  ],
  en: [
    { href: "/download", label: "Download" },
    { href: "/community", label: "Community" },
    { href: "/restaurants", label: "For restaurants" },
    { href: "/press?lang=en", label: "Press" },
    { href: "/support", label: "Support" },
    { href: "/privacy", label: "Privacy" },
    { href: "/terms", label: "Terms" },
  ],
  ca: [
    { href: "/download", label: "Descarregar" },
    { href: "/community", label: "Comunitat" },
    { href: "/restaurants", label: "Restaurants" },
    { href: "/press", label: "Premsa" },
    { href: "/support", label: "Suport" },
    { href: "/privacy", label: "Privacitat" },
    { href: "/terms", label: "Termes" },
  ],
};

const SOCIAL_NAV_ARIA: Record<MarketingLocale, string> = {
  es: "Redes sociales de MenúHunter",
  en: "MenúHunter social links",
  ca: "Xarxes socials de MenúHunter",
};

type MarketingFooterProps = {
  /** SEO landing pages pass URL locale; other pages use client language. */
  locale?: MarketingLocale;
  /** Press kit: language links stay on /press and do not change the saved site language. */
  pressLang?: PressLang;
};

export function MarketingFooter({ locale: localeProp, pressLang }: MarketingFooterProps) {
  const { language, t } = useLanguage();
  const locale = localeProp ?? language;
  const links = FOOTER_BY_LOCALE[locale];
  const footerNavAria = t.common?.footerNav ?? "Footer";

  return (
    <footer className="border-t border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50 py-8 flex-shrink-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav
          className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-slate-600 dark:text-slate-400 mb-5"
          aria-label={footerNavAria}
        >
          {links.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="hover:text-slate-900 dark:hover:text-slate-200 transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <BrandSocialIcons
          className="mb-5"
          ariaLabel={SOCIAL_NAV_ARIA[locale]}
        />
        <div className="mb-5 flex justify-center">
          {pressLang ? (
            <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center sm:gap-3">
              <span className="text-xs font-medium text-slate-400">
                {PRESS_COPY[pressLang].languageLabel}
              </span>
              <PressLanguageToggle lang={pressLang} variant="footer" />
            </div>
          ) : (
            <PublicFooterLanguage />
          )}
        </div>
        <p className="text-center text-xs text-slate-500">
          &copy; {new Date().getFullYear()} MenúHunter™
        </p>
      </div>
    </footer>
  );
}
